import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
    Checkbox,
    Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import axios from 'axios';
import { useSelector } from 'react-redux';

const STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'new', label: 'New' },
    { value: 'open', label: 'Open' },
    { value: 'pending', label: 'Pending' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
];

const PRIORITY_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
];

const MAX_ATTACHMENTS = 10;

const statusChipColor = {
    new: 'default',
    open: 'warning',
    pending: 'info',
    resolved: 'success',
    closed: 'default',
};

const priorityChipColor = {
    low: 'success',
    medium: 'primary',
    high: 'warning',
    urgent: 'error',
};

const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const TicketListEmptyState = () => (
    <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed #cfd8dc' }}>
        <HelpOutlineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
            No support tickets yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
            Submit a ticket using the form above and it will appear here for quick access.
        </Typography>
    </Paper>
);

const ReplyBubble = ({ reply }) => {
    const isAgent = reply.authorType === 'agent';
    const isInternal = reply.internal;
    const bubbleBg = isInternal ? '#f3e5f5' : isAgent ? '#e3f2fd' : '#e8f5e9';
    const borderColor = isInternal ? '#ce93d8' : isAgent ? '#64b5f6' : '#81c784';
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 2,
                borderLeft: `4px solid ${borderColor}`,
                backgroundColor: bubbleBg,
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <Typography variant="subtitle2">{reply.authorName || 'Support User'}</Typography>
                <FiberManualRecordIcon sx={{ fontSize: 8, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                    {formatDateTime(reply.createdAt)}
                </Typography>
                {isInternal && (
                    <Chip size="small" label="Internal" color="secondary" variant="outlined" sx={{ ml: 1 }} />
                )}
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                {reply.message}
            </Typography>
            {Array.isArray(reply.attachments) && reply.attachments.length > 0 && (
                <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                    {reply.attachments.map((attachment) => (
                        <Chip
                            key={`${reply.id}-${attachment.url}`}
                            component="a"
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            label={attachment.name}
                            clickable
                            icon={<ChatBubbleOutlineIcon fontSize="small" />}
                            sx={{ maxWidth: 240 }}
                        />
                    ))}
                </Stack>
            )}
        </Paper>
    );
};

const SupportTicketWorkspace = ({ categories = [], initialTicketId, onTicketSelect }) => {
    const { token } = useSelector((state) => state.auth);

    const [tickets, setTickets] = useState([]);
    const [stats, setStats] = useState({ status: {}, priority: {}, total: 0 });
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [ticketsError, setTicketsError] = useState('');

    const [selectedTicketId, setSelectedTicketId] = useState(initialTicketId || '');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [thread, setThread] = useState([]);
    const [threadLoading, setThreadLoading] = useState(false);
    const [threadError, setThreadError] = useState('');

    const [filters, setFilters] = useState({ status: 'all', priority: 'all', search: '' });
    const [searchDraft, setSearchDraft] = useState('');

    const [formValues, setFormValues] = useState({
        subject: '',
        category: '',
        priority: 'medium',
        message: '',
        tags: '',
    });
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [formAttachments, setFormAttachments] = useState([]);
    const [formAttachmentUploading, setFormAttachmentUploading] = useState(false);
    const [formAttachmentError, setFormAttachmentError] = useState('');

    const [replyMessage, setReplyMessage] = useState('');
    const [markResolved, setMarkResolved] = useState(false);
    const [replySubmitting, setReplySubmitting] = useState(false);
    const [replyError, setReplyError] = useState('');
    const [replyAttachments, setReplyAttachments] = useState([]);
    const [replyAttachmentUploading, setReplyAttachmentUploading] = useState(false);
    const [replyAttachmentError, setReplyAttachmentError] = useState('');

    const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

    const categoryOptions = useMemo(() => {
        const base = categories.map((category) => ({ value: category.name, label: category.name }));
        const names = base.map((item) => item.value.toLowerCase());
        if (!names.includes('general')) {
            base.unshift({ value: 'General', label: 'General' });
        }
        return base;
    }, [categories]);

    const uploadAttachment = useCallback(
        async (file) => {
            if (!file) return null;
            const formData = new FormData();
            formData.append('file', file);
            const { data } = await axios.post('/api/support/tickets/attachments', formData, {
                headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
            });
            return data?.attachment || null;
        },
        [authHeaders]
    );

    const handleFormAttachmentChange = useCallback(
        async (event) => {
            const files = Array.from(event.target.files || []);
            event.target.value = '';
            if (!files.length) return;

            const availableSlots = MAX_ATTACHMENTS - formAttachments.length;
            if (availableSlots <= 0) {
                setFormAttachmentError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
                return;
            }

            let filesToUpload = files;
            if (files.length > availableSlots) {
                filesToUpload = files.slice(0, availableSlots);
                setFormAttachmentError(
                    `Only ${availableSlots} more attachment${availableSlots === 1 ? '' : 's'} can be added.`
                );
            } else {
                setFormAttachmentError('');
            }

            setFormAttachmentUploading(true);
            try {
                const uploaded = [];
                for (const file of filesToUpload) {
                    // eslint-disable-next-line no-await-in-loop
                    const attachment = await uploadAttachment(file);
                    if (attachment) {
                        uploaded.push(attachment);
                    }
                }
                if (uploaded.length) {
                    setFormAttachments((prev) => [...prev, ...uploaded]);
                }
            } catch (error) {
                const message = error.response?.data?.msg || error.message || 'Failed to upload attachment.';
                setFormAttachmentError(message);
            } finally {
                setFormAttachmentUploading(false);
            }
        },
        [formAttachments.length, uploadAttachment]
    );

    const handleRemoveFormAttachment = useCallback((url) => {
        setFormAttachments((prev) => prev.filter((attachment) => attachment.url !== url));
    }, []);

    const handleReplyAttachmentChange = useCallback(
        async (event) => {
            const files = Array.from(event.target.files || []);
            event.target.value = '';
            if (!files.length) return;

            const availableSlots = MAX_ATTACHMENTS - replyAttachments.length;
            if (availableSlots <= 0) {
                setReplyAttachmentError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
                return;
            }

            let filesToUpload = files;
            if (files.length > availableSlots) {
                filesToUpload = files.slice(0, availableSlots);
                setReplyAttachmentError(
                    `Only ${availableSlots} more attachment${availableSlots === 1 ? '' : 's'} can be added.`
                );
            } else {
                setReplyAttachmentError('');
            }

            setReplyAttachmentUploading(true);
            try {
                const uploaded = [];
                for (const file of filesToUpload) {
                    // eslint-disable-next-line no-await-in-loop
                    const attachment = await uploadAttachment(file);
                    if (attachment) {
                        uploaded.push(attachment);
                    }
                }
                if (uploaded.length) {
                    setReplyAttachments((prev) => [...prev, ...uploaded]);
                }
            } catch (error) {
                const message = error.response?.data?.msg || error.message || 'Failed to upload attachment.';
                setReplyAttachmentError(message);
            } finally {
                setReplyAttachmentUploading(false);
            }
        },
        [replyAttachments.length, uploadAttachment]
    );

    const handleRemoveReplyAttachment = useCallback((url) => {
        setReplyAttachments((prev) => prev.filter((attachment) => attachment.url !== url));
    }, []);

    const fetchStats = useCallback(async () => {
        if (!token) return;
        setStatsLoading(true);
        try {
            const { data } = await axios.get('/api/support/tickets/stats', { headers: authHeaders });
            setStats(data || { status: {}, priority: {}, total: 0 });
        } catch (error) {
            console.error('Failed to fetch ticket stats', error);
        } finally {
            setStatsLoading(false);
        }
    }, [token, authHeaders]);

    const fetchTickets = useCallback(async () => {
        if (!token) return;
        setTicketsLoading(true);
        setTicketsError('');
        try {
            const params = {};
            if (filters.status !== 'all') params.status = filters.status;
            if (filters.priority !== 'all') params.priority = filters.priority;
            if (filters.search) params.search = filters.search;
            const { data } = await axios.get('/api/support/tickets', { headers: authHeaders, params });
            setTickets(data?.tickets || []);
        } catch (error) {
            console.error('Failed to load support tickets', error);
            setTicketsError(error?.response?.data?.msg || 'Failed to load support tickets.');
        } finally {
            setTicketsLoading(false);
        }
    }, [token, authHeaders, filters.status, filters.priority, filters.search]);

    const fetchTicketDetails = useCallback(
        async (ticketId) => {
            if (!token || !ticketId) return;
            setThreadLoading(true);
            setThreadError('');
            try {
                const { data } = await axios.get(`/api/support/tickets/${ticketId}`, { headers: authHeaders });
                setSelectedTicket(data.ticket || null);
                setThread(data.replies || []);
            } catch (error) {
                console.error('Failed to load ticket details', error);
                setThreadError(error?.response?.data?.msg || 'Failed to load ticket details.');
            } finally {
                setThreadLoading(false);
            }
        },
        [token, authHeaders]
    );

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    useEffect(() => {
        setReplyAttachments([]);
        setReplyAttachmentError('');
        setReplyAttachmentUploading(false);
    }, [selectedTicketId]);

    useEffect(() => {
        if (initialTicketId && initialTicketId !== selectedTicketId) {
            setSelectedTicketId(initialTicketId);
        }
    }, [initialTicketId, selectedTicketId]);

    useEffect(() => {
        if (!selectedTicketId && tickets.length > 0) {
            const firstTicketId = tickets[0].id;
            setSelectedTicketId(firstTicketId);
            onTicketSelect?.(firstTicketId);
            return;
        }
        if (selectedTicketId) {
            fetchTicketDetails(selectedTicketId);
        } else {
            setSelectedTicket(null);
            setThread([]);
            onTicketSelect?.('');
        }
    }, [selectedTicketId, tickets.length, fetchTicketDetails, onTicketSelect]);

    const handleApplySearch = () => {
        setFilters((prev) => ({ ...prev, search: searchDraft.trim() }));
    };

    const handleClearFilters = () => {
        setFilters({ status: 'all', priority: 'all', search: '' });
        setSearchDraft('');
    };

    const handleCreateTicket = async (event) => {
        event.preventDefault();
        if (!token) return;
        if (formAttachmentUploading) {
            setFormError('Please wait for attachments to finish uploading.');
            return;
        }
        setFormSubmitting(true);
        setFormError('');
        setFormSuccess('');
        try {
            const payload = {
                subject: formValues.subject.trim(),
                priority: formValues.priority,
                category: formValues.category || 'General',
                message: formValues.message.trim(),
            };
            if (!payload.subject) {
                throw new Error('Please provide a subject for your request.');
            }
            if (!payload.message) {
                throw new Error('Describe your issue in the message field.');
            }
            if (formValues.tags) {
                payload.tags = formValues.tags
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter(Boolean);
            }
            if (formAttachments.length) {
                payload.attachments = formAttachments;
            }
            const { data } = await axios.post('/api/support/tickets', payload, { headers: authHeaders });
            const createdTicket = data?.ticket;
            if (createdTicket) {
                setTickets((prev) => [createdTicket, ...prev]);
                setSelectedTicketId(createdTicket.id);
                setSelectedTicket(createdTicket);
                onTicketSelect?.(createdTicket.id);
                setThread(data?.reply ? [data.reply] : []);
                fetchStats();
                setFormSuccess('Support ticket submitted successfully. Our team will reach out soon.');
                setFormValues({ subject: '', category: '', priority: 'medium', message: '', tags: '' });
                setFormAttachments([]);
                setFormAttachmentError('');
            }
        } catch (error) {
            const message = error.response?.data?.msg || error.message || 'Failed to submit support ticket.';
            setFormError(message);
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleSendReply = async () => {
        if (!token || !selectedTicketId) return;
        if (!replyMessage.trim()) {
            setReplyError('Add a message before sending your reply.');
            return;
        }
        if (replyAttachmentUploading) {
            setReplyError('Please wait for attachments to finish uploading.');
            return;
        }
        setReplySubmitting(true);
        setReplyError('');
        try {
            const payload = { message: replyMessage.trim() };
            if (replyAttachments.length) {
                payload.attachments = replyAttachments;
            }
            if (markResolved) {
                payload.status = 'resolved';
            }
            const { data } = await axios.post(`/api/support/tickets/${selectedTicketId}/replies`, payload, {
                headers: authHeaders,
            });
            if (data?.reply) {
                setThread((prev) => [...prev, data.reply]);
            }
            if (data?.ticket) {
                setSelectedTicket(data.ticket);
                onTicketSelect?.(data.ticket.id);
                setTickets((prev) => prev.map((ticket) => (ticket.id === data.ticket.id ? data.ticket : ticket)));
                fetchStats();
            }
            setReplyMessage('');
            setMarkResolved(false);
            setReplyAttachments([]);
            setReplyAttachmentError('');
        } catch (error) {
            const message = error.response?.data?.msg || 'Failed to send reply.';
            setReplyError(message);
        } finally {
            setReplySubmitting(false);
        }
    };

    const handleRefreshTickets = () => {
        fetchTickets();
        fetchStats();
        if (selectedTicketId) {
            fetchTicketDetails(selectedTicketId);
        }
    };

    const activeStatusCount = (statusValue) => stats?.status?.[statusValue] || 0;

    return (
        <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e0e0e0', borderRadius: 3 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                {STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                                    <Chip
                                        key={option.value}
                                        label={`${option.label} (${activeStatusCount(option.value)})`}
                                        color={filters.status === option.value ? 'primary' : statusChipColor[option.value] || 'default'}
                                        onClick={() => setFilters((prev) => ({ ...prev, status: option.value }))}
                                    />
                                ))}
                                {statsLoading && <CircularProgress size={18} sx={{ ml: 0.5 }} />}
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ ml: 'auto' }} alignItems="center">
                                <TextField
                                    size="small"
                                    select
                                    label="Priority"
                                    value={filters.priority}
                                    onChange={(event) => setFilters((prev) => ({ ...prev, priority: event.target.value }))}
                                    sx={{ minWidth: 160 }}
                                >
                                    {PRIORITY_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    size="small"
                                    label="Search tickets"
                                    value={searchDraft}
                                    onChange={(event) => setSearchDraft(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            handleApplySearch();
                                        }
                                    }}
                                    sx={{ minWidth: 220 }}
                                />
                                <Button variant="contained" onClick={handleApplySearch} sx={{ whiteSpace: 'nowrap' }}>
                                    Apply
                                </Button>
                                <Tooltip title="Refresh tickets">
                                    <IconButton onClick={handleRefreshTickets}>
                                        <RefreshIcon />
                                    </IconButton>
                                </Tooltip>
                                <Button variant="text" color="inherit" onClick={handleClearFilters} sx={{ whiteSpace: 'nowrap' }}>
                                    Clear
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Stack spacing={3}>
                        <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 3 }}>
                            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                <AddCircleOutlineIcon color="primary" />
                                <Typography variant="h6">Open a new ticket</Typography>
                            </Stack>
                            {formError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {formError}
                                </Alert>
                            )}
                            {formSuccess && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    {formSuccess}
                                </Alert>
                            )}
                            <Box component="form" onSubmit={handleCreateTicket}>
                                <TextField
                                    label="Subject"
                                    value={formValues.subject}
                                    onChange={(event) =>
                                        setFormValues((prev) => ({ ...prev, subject: event.target.value }))
                                    }
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    select
                                    label="Category"
                                    value={formValues.category}
                                    onChange={(event) =>
                                        setFormValues((prev) => ({ ...prev, category: event.target.value }))
                                    }
                                    fullWidth
                                    margin="normal"
                                >
                                    {categoryOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    select
                                    label="Priority"
                                    value={formValues.priority}
                                    onChange={(event) =>
                                        setFormValues((prev) => ({ ...prev, priority: event.target.value }))
                                    }
                                    fullWidth
                                    margin="normal"
                                >
                                    {PRIORITY_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="How can we help?"
                                    value={formValues.message}
                                    onChange={(event) =>
                                        setFormValues((prev) => ({ ...prev, message: event.target.value }))
                                    }
                                    fullWidth
                                    margin="normal"
                                    multiline
                                    minRows={4}
                                />
                                <TextField
                                    label="Tags (comma separated)"
                                    value={formValues.tags}
                                    onChange={(event) =>
                                        setFormValues((prev) => ({ ...prev, tags: event.target.value }))
                                    }
                                    fullWidth
                                    margin="normal"
                                    placeholder="billing, urgent"
                                />
                                <Stack spacing={1} mt={1}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            component="label"
                                            startIcon={<AttachFileIcon />}
                                            disabled={
                                                formAttachmentUploading ||
                                                formAttachments.length >= MAX_ATTACHMENTS
                                            }
                                        >
                                            {formAttachmentUploading ? 'Uploading…' : 'Add attachments'}
                                            <input
                                                type="file"
                                                hidden
                                                multiple
                                                onChange={handleFormAttachmentChange}
                                            />
                                        </Button>
                                        {formAttachmentUploading && <CircularProgress size={20} />}
                                        <Typography variant="caption" color="text.secondary">
                                            Up to {MAX_ATTACHMENTS} files, 20MB each.
                                        </Typography>
                                    </Stack>
                                    {formAttachmentError && <Alert severity="error">{formAttachmentError}</Alert>}
                                    {formAttachments.length > 0 && (
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {formAttachments.map((attachment) => (
                                                <Chip
                                                    key={attachment.url}
                                                    component="a"
                                                    href={attachment.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    label={attachment.name}
                                                    clickable
                                                    onDelete={() => handleRemoveFormAttachment(attachment.url)}
                                                    sx={{ maxWidth: 240 }}
                                                />
                                            ))}
                                        </Stack>
                                    )}
                                </Stack>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    sx={{ mt: 2 }}
                                    disabled={formSubmitting || formAttachmentUploading}
                                >
                                    {formSubmitting ? 'Submitting...' : 'Submit Ticket'}
                                </Button>
                            </Box>
                        </Paper>

                        <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 3, overflow: 'hidden' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
                                <Typography variant="h6">My tickets</Typography>
                                {ticketsLoading && <CircularProgress size={20} />}
                            </Stack>
                            <Divider />
                            {ticketsError && (
                                <Alert severity="error" sx={{ m: 2 }}>
                                    {ticketsError}
                                </Alert>
                            )}
                            {!ticketsLoading && tickets.length === 0 ? (
                                <TicketListEmptyState />
                            ) : (
                                <List sx={{ maxHeight: 420, overflowY: 'auto' }}>
                                    {tickets.map((ticket) => (
                                        <ListItem disablePadding key={ticket.id}>
                                            <ListItemButton
                                                selected={ticket.id === selectedTicketId}
                                                onClick={() => {
                                                    setSelectedTicketId(ticket.id);
                                                    onTicketSelect?.(ticket.id);
                                                }}
                                                alignItems="flex-start"
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Typography variant="subtitle2" noWrap>
                                                                {ticket.subject}
                                                            </Typography>
                                                            <Chip
                                                                size="small"
                                                                label={ticket.status}
                                                                color={statusChipColor[ticket.status] || 'default'}
                                                            />
                                                        </Stack>
                                                    }
                                                    secondary={
                                                        <>
                                                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                                                #{ticket.ticketNumber} • {ticket.priority} priority
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                                Updated {formatDateTime(ticket.lastActivityAt)}
                                                            </Typography>
                                                        </>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Paper>
                    </Stack>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 3, minHeight: 560 }}>
                        {threadLoading && (
                            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                                <CircularProgress />
                            </Stack>
                        )}
                        {!threadLoading && !selectedTicket && (
                            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }} spacing={2}>
                                <HelpOutlineIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                                <Typography variant="h6">Select a ticket to view the conversation</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    All of your conversations with the support team will appear here.
                                </Typography>
                            </Stack>
                        )}
                        {!threadLoading && selectedTicket && (
                            <>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                                    <Box>
                                        <Typography variant="h6">{selectedTicket.subject}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Ticket #{selectedTicket.ticketNumber} • Created {formatDateTime(selectedTicket.createdAt)}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
                                        <Chip
                                            label={selectedTicket.status}
                                            color={statusChipColor[selectedTicket.status] || 'default'}
                                        />
                                        <Chip
                                            label={`${selectedTicket.priority} priority`}
                                            color={priorityChipColor[selectedTicket.priority] || 'default'}
                                        />
                                    </Stack>
                                </Stack>

                                <Divider sx={{ my: 3 }} />

                                {threadError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {threadError}
                                    </Alert>
                                )}

                                <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }}>
                                    {thread.map((reply) => (
                                        <ReplyBubble key={reply.id} reply={reply} />
                                    ))}
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                <Stack spacing={1}>
                                    {replyError && <Alert severity="error">{replyError}</Alert>}
                                    <TextField
                                        label="Reply"
                                        placeholder="Type your response for our support team"
                                        value={replyMessage}
                                        onChange={(event) => setReplyMessage(event.target.value)}
                                        multiline
                                        minRows={3}
                                        fullWidth
                                    />
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                component="label"
                                                startIcon={<AttachFileIcon />}
                                                disabled={
                                                    replyAttachmentUploading ||
                                                    replyAttachments.length >= MAX_ATTACHMENTS
                                                }
                                            >
                                                {replyAttachmentUploading ? 'Uploading…' : 'Add files'}
                                                <input
                                                    type="file"
                                                    hidden
                                                    multiple
                                                    onChange={handleReplyAttachmentChange}
                                                />
                                            </Button>
                                            {replyAttachmentUploading && <CircularProgress size={20} />}
                                            <Typography variant="caption" color="text.secondary">
                                                Up to {MAX_ATTACHMENTS} files, 20MB each.
                                            </Typography>
                                        </Stack>
                                        {replyAttachmentError && (
                                            <Alert severity="error">{replyAttachmentError}</Alert>
                                        )}
                                        {replyAttachments.length > 0 && (
                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {replyAttachments.map((attachment) => (
                                                    <Chip
                                                        key={attachment.url}
                                                        component="a"
                                                        href={attachment.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        label={attachment.name}
                                                        clickable
                                                        onDelete={() => handleRemoveReplyAttachment(attachment.url)}
                                                        sx={{ maxWidth: 240 }}
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                    </Stack>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={markResolved}
                                                onChange={(event) => setMarkResolved(event.target.checked)}
                                            />
                                        }
                                        label="Mark this ticket as resolved"
                                    />
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button
                                            variant="contained"
                                            endIcon={<SendIcon />}
                                            onClick={handleSendReply}
                                            disabled={replySubmitting || replyAttachmentUploading}
                                        >
                                            {replySubmitting ? 'Sending...' : 'Send Reply'}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SupportTicketWorkspace;
